import { useTextTranslation } from "../../utils/useTextTranslation";
import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { flowCopy } from "../../utils/flowCopy";
import Modal from "../modal/Modal";
import localizationStrings from "../../statics/localization";
import { SessionContext } from "../../context/SessionContext";
import ImageView from "../imageView";
import defaultPoster from "../../assets/images/default-vod-horizontal-poster.png";
import Notification from "../notification";
import ConfirmPayment from "../confirmPayment";
import { getPosterUrlByType } from "../../utils/findPoster";
import { sendPayment } from "../../api/vod/vod";
import { isPayableOffer, publicationEnd, rentalCopy } from "../../api/mw/rental";
import { currentProfile } from "../../api/mw/client";

// PIN validation updates the saved profile's PIN, so compare identity rather
// than the whole profile object. Tokens also distinguish accounts and logins.
const paymentContext = (profileId) => JSON.stringify([
  profileId || null,
  currentProfile().id || null,
  localStorage.getItem("userToken"),
  localStorage.getItem("sessionid")
]);

// Submit the CMS offer once; a timeout must never trigger a second charge.
const Payment = ({ vod, product, closePayment, onRefresh }) => {
  const translateText = useTextTranslation();
  const mounted = useRef(true);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const paymentPending = useRef(false);
  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showCreatePinNotification, setShowCreatePinNotification] =
    useState(false);
  const { profile } = useContext(SessionContext);
  const profileId = profile?.id;
  const activeProfile = useRef(profileId);
  const openingContext = useRef(paymentContext(profileId));
  useLayoutEffect(() => {
    activeProfile.current = profileId;
  }, [profileId]);
  const history = useHistory();
  const copy = rentalCopy();
  const ensureCurrent = () => {
    if (!mounted.current) return false;
    if (openingContext.current === paymentContext(activeProfile.current)) return true;
    setShowPinModal(false);
    setPaymentError({ message: copy.contextChanged });
    return false;
  };
  const needsConsent = product?.RequiresPublicationEndConfirmation === true;
  const validOffer =
    isPayableOffer(product) &&
    (!needsConsent || !!publicationEnd(product));
  const offerError =
    !validOffer
      ? copy.invalidOffer
      : null;
  const close = () => {
    if (!paying) {
      if (closePayment) closePayment(false);
      else history.push("/dashboard/purchasehistory");
    }
  };
  const refresh = () => {
    closePayment(false);
    if (onRefresh) onRefresh();
  };

  const executePayment = async () => {
    if (
      !validOffer ||
      (needsConsent && !acknowledged) ||
      paymentPending.current ||
      !ensureCurrent()
    )
      return;
    paymentPending.current = true;
    setPaying(true);
    setPaymentError(null);
    try {
      const response = await sendPayment(
        vod.id,
        product.id,
        {
          QuoteToken: product.QuoteToken,
          PublicationEndAcknowledged: needsConsent && acknowledged
        },
        product.type
      );
      if (!ensureCurrent()) return;
      // The API rejects business failures even when HTTP status is 200.
      if (response.status !== 200)
        throw new Error("Unexpected rental response");
      closePayment(true, response.data);
    } catch (error) {
      if (!ensureCurrent()) return;
      const code = Number(error.code);
      if ([7002, 7004, 7005, 7006].includes(code)) {
        setPaymentError({
          refresh: true,
          message: code === 7006 ? copy.changed : copy.missing
        });
      } else {
        // A timeout or 7003 may follow a charge. Never automatically resend it.
        setPaymentError({
          history: true,
          message:
            code === 7007
              ? copy.inProgress
              : [7003].includes(code)
              ? `${error.message || copy.failed} ${flowCopy().paymentError}`
              : flowCopy().paymentError
        });
      }
    } finally {
      if (mounted.current) setPaying(false);
    }
  };

  const confirm = () => {
    if (paymentPending.current || offerError || (needsConsent && !acknowledged))
      return;
    if (!ensureCurrent()) return;
    if (profile?.hasPin) setShowPinModal(true);
    else setShowCreatePinNotification(true);
  };

  return (
    <Modal
      viewClass="payment-modal rental-payment-modal"
      rootClass="rental-payment-root"
      onClose={paying ? undefined : close}
      label={product?.type === "RENTAL" ? copy.confirmTitle : localizationStrings.rentVod}
    >
      <div className="payment-container">
        <header className="rental-payment-header">
          <h2>{product?.type === "RENTAL" ? copy.confirmTitle : localizationStrings.rentVod}</h2>
          <button type="button" className="rental-payment-close" aria-label={localizationStrings.close}
            disabled={paying || showPinModal} onClick={close}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </header>
        <div className="rental-payment-content">
          <div className="vod-poster" aria-hidden="true">
            <ImageView
              src={getPosterUrlByType(vod?.posters, "Web Landscape") || getPosterUrlByType(vod?.posters, "Web Portrait")}
              defaultPoster={defaultPoster}
            />
          </div>
          <h3>{translateText(vod?.title)}</h3>
        </div>
        <div className="rental-payment-body">
          {offerError || paymentError ? (
            <div className="rental-result">
              <p role="alert">{offerError || paymentError.message}</p>
              <div className="rental-result-actions">
                {(paymentError?.refresh ||
                  (offerError && product?.type !== "PURCHASE")) &&
                  onRefresh && (
                    <button className="confirm-button" onClick={refresh}>
                      {copy.refresh}
                    </button>
                  )}
                {paymentError?.history && (
                  <button
                    className="confirm-button"
                    onClick={() => {
                      closePayment(false);
                      history.push("/dashboard/purchasehistory");
                    }}
                  >
                    {copy.history}
                  </button>
                )}
                <button className="not-confirm-button" onClick={close}>
                  {localizationStrings.close}
                </button>
              </div>
            </div>
          ) : (
            <ConfirmPayment
              vod={vod}
              product={product}
              pending={paying || showPinModal}
              acknowledged={acknowledged}
              onAcknowledgedChange={setAcknowledged}
              confirm={confirm}
              notConfirm={close}
              compact={true}
            />
          )}
        </div>
      </div>
      {showPinModal && (
        <Notification
          text={localizationStrings.profile.enterProfilePasswordToBuyRent}
          confirmText={localizationStrings.enter}
          showCloseButton={true}
          logo={true}
          theme="no-bg"
          isPassword={true}
          isCurrent={ensureCurrent}
          onClickNotConfirm={() => setShowPinModal(false)}
          sendResult={(valid) => {
            if (valid && mounted.current) {
              setShowPinModal(false);
              executePayment();
            }
          }}
        />
      )}
      {showCreatePinNotification && (
        <Notification
          text={localizationStrings.createProfilePinCodeText}
          confirmText={localizationStrings.createProfilePinCode}
          notConfirmText={localizationStrings.cancel}
          onClickConfirm={() => {
            closePayment(false);
            history.push(`/profile/${profile?.id}?page=1`);
          }}
          onClickNotConfirm={() => setShowCreatePinNotification(false)}
        />
      )}
    </Modal>
  );
};

export default Payment;
